/**
 * 历史搜索卡片
 * 
 * @Author: Focci
 * @Date: 2023-08-23 14:07:29
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-23 14:07:29
 */

'use client'

import PropTypes from 'prop-types'
import PropertyIcons from '@comp/page/common/card/property/PropertyIcons'
import SVGClock from '@img/clock.svg'
import { getPriceLabel } from '@comp/page/common/filter/listing/filter-bar/lib'
import { forEach, isArray, map, size, isObject } from 'lodash-es'
import { Skeleton, Tooltip } from '@comp/material'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { countryAlias, houseSearchType } from '@lib/config'
import HistoryClaimIcon from './HistoryClaimIcon'

export function HistoryCardSkeleton() {
  return (
    <div className="border rounded-md px-3 relative w-full h-18">
      <div className="flex items-center gap-x-2 mt-2">
        <Skeleton 
          variant="text" 
          style={{ width: '1.5rem', fontSize: '.9rem' }} 
        />
        <Skeleton 
          variant="text" 
          style={{ width: '80%', fontSize: '.9rem' }} 
        />
      </div>
      <div className="mt-2">
        <Skeleton variant="text" style={{ width: '60%' }} />
      </div>
    </div>
  )
}

export function HistoryCardSkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4">
      <HistoryCardSkeleton />
    </div>
  )
}

/**
 * 获取历史记录名称
 * th = useTranslations('home')
 */
export function getHistoryName(item, th) {
  const { data } = item
  const names = []

  if(item.type === 'listing') {
    return { name: item.name, names: [item.name] }
  }
  
  forEach(
    ['region', 'district', 'suburb', 'street', 'school', 'listing'],
    (key) => {
      if(isArray(data[key])) {
        names.push(...map(data[key], 'label'))
      } else if(isObject(data[key])) {
        names.push(data[key].label)
      }
    }
  )
  
  let name = names.slice(0, 2).join(', ')
  if(!name) {
    name = process.env.NEXT_PUBLIC_COUNTRY === countryAlias.nz
      ? th('nzAll')
      : th('auAll')
  }
  
  return { name, names }
}

/**
 * 获取历史价格
 */
export function getHistoryPriceLabel(data) {
  const { type, data: searchParam } = data
  
  return isArray(searchParam.price) && searchParam.price.length > 0
    ? getPriceLabel(searchParam.price, type)
    : ''
}

/**
 * 获取历史记录的房间、浴室数据
 */
export function getHistoryPropertyIconData(data) {
  const tmp = {}
  const target = data || {}

  forEach(['bathroom', 'bedroom'], (key) => {
    if(isArray(target[key]) && target[key].length > 0) {
      tmp[`${key}s`] = target[key].join(',')
    }
  })

  return tmp
}

function SubcribeButton({ subcribe, ...props }) {
  const th = useTranslations('home')
  const tt = useTranslations('track')

  return (
    <Tooltip
      arrow
      placement="top"
      title={subcribe ? th('subcribed') : th('subcribedMeta')}
    >
      <div className="ml-4 shrink-0 flex flex-col items-center gap-1">
        <HistoryClaimIcon
          subcribe={subcribe}
          {...props}
        />
        <span className="text-f.6 text-helper -mb-2">
          {subcribe ? th('subcribed') : tt('subcribe')}
        </span>
      </div>
    </Tooltip>
  )
}

export default function HistoryCard({
  data = {},
  typeLabel = '',
  unreadCount = 0, // 未查看房源数量
  subcribe,
  onClick = () => {}, // 点击
  onUpdateSubcribe = () => {}, // 点击
}) {
  const th = useTranslations('home')

  const { names, name } = useMemo(() => getHistoryName(data, th), [data, th])
  const historyCardName = useMemo(() => (data?.isSubcribe ? data.name : name), [name, data])
  const propertyIconData = useMemo(() => getHistoryPropertyIconData(data.data), [data])
  const priceLabel = useMemo(() => getHistoryPriceLabel(data), [data])

  const categoryLabel = useMemo(() => {
    const { ptnCategory } = data?.data || {}
    const tmp = isArray(ptnCategory) ? map(ptnCategory, 'label') : []

    return tmp.join(', ')
  }, [data])

  // 是否显示订阅图标
  const showSubcribeButton = useMemo(() => {
    const { type, isSubcribe } = data
    return type !== 'agents'
      && type !== 'listing'
      && !isSubcribe
      && [houseSearchType.sold, houseSearchType.estimate].indexOf(type) === -1
  }, [data])
  
  return (
    <div 
      className="
        flex items-center border rounded-md box-border h-18
        px-3 relative transition-colors hover:bg-cf0
      "
    >
      <div 
        className="flex-auto cursor-pointer overflow-hidden"
        onClick={onClick}
        onKeyPress={onClick}
        tabIndex={0}
        role="button"
      >
        <div className="flex items-center gap-x-2">
          <span className="shrink-0">
            <SVGClock />
          </span>
          <div className="flex items-center gap-x-1 overflow-hidden flex-auto">
            {unreadCount > 0 && (
              <span 
                className="
                  bg-error rounded-md text-white whitespace-nowrap text-f.6
                  h-4 px-1 flex items-center
                "
              >
                {th('newPropertyCount', { count: unreadCount > 99 ? '99+' : unreadCount })}
              </span>
            )}
            <b className="text-f.9 line-clamp-1 text-left capitalize flex-auto">
              {historyCardName}
              {!data.isSubcribe && names.length > 2 ? '...' : ''}
            </b>
            {!data.isSubcribe && names.length > 2 && (
              <b className="text-f.9 whitespace-nowrap">
                {th('totalNumber', { n: names.length })}
              </b>
            )}
          </div>
        </div>
        <div className="text-meta mt-2 flex items-center gap-x-2">
          <span className="whitespace-nowrap">{typeLabel}</span>
          {categoryLabel && (
            <>
              <span className="h-1 w-1 bg-cd8 rounded-full" />
              <span className="whitespace-nowrap">{categoryLabel}</span>
            </>
          )}
          {priceLabel && (
            <>
              <span className="h-1 w-1 bg-cd8 rounded-full" />
              <span>{priceLabel}</span>
            </>
          )}
          {size(propertyIconData) > 0 && (
            <>
              <span className="h-1 w-1 bg-cd8 rounded-full" />
              <PropertyIcons data={propertyIconData} />
            </>
          )}
        </div>
      </div>
      {showSubcribeButton && (
        <SubcribeButton
          subcribe={subcribe}
          onUpdateSubcribe={onUpdateSubcribe}
          data={data}
          name={historyCardName}
        />
      )}
    </div>
  )
}

HistoryCard.propTypes = {
  data: PropTypes.object,
  typeLabel: PropTypes.string,
  unreadCount: PropTypes.number,
  onClick: PropTypes.func,
}
